// catalog_manager "Incoming requests" list — route /incoming-requests
// (RequireRole(['catalog_manager']) only, App.jsx). Reads GET
// /catalog-manager/requests: id/ref_no/needed_by/line_count per row, no
// organization identity, no status, no assignee — this role's read never
// carries any of that (see hooks/useCatalogManagerRequests.js and
// backend/src/routes/catalogManagerRequestRoutes.js's own header comment
// for the exact scope). A brand new, deliberately narrow screen — NOT a
// reuse of AllRequestsPage.jsx, which renders organization/status/assignee
// columns this role must never see even behind a client-side gate.
//
// Ledger-style: one ruled table, no card-per-row, no sidebar (root
// CLAUDE.md / admin/CLAUDE.md), same shape as AllRequestsPage. Each row
// links to the matching detail screen at /incoming-requests/:requestId.

import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext.jsx';
import { useCatalogManagerRequests } from '../hooks/useCatalogManagerRequests.js';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import { formatDate } from '../utils/formatDate.js';
import styles from './CatalogManagerRequestsPage.module.css';

export default function CatalogManagerRequestsPage() {
  const { t, lang } = useLanguage();
  const { data: requests, isLoading, isError, error, refetch } = useCatalogManagerRequests();

  const allRequests = requests || [];
  const isTrulyEmpty = !isLoading && !isError && allRequests.length === 0;

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t('catalogManagerRequests.title')}</h1>
      <p className={styles.subtitle}>{t('catalogManagerRequests.subtitle')}</p>
      <hr className="hairline" />

      {isLoading && <LoadingState variant="rows" rows={6} />}

      {isError && !isLoading && (
        <ErrorState messageId="catalogManagerRequests.errorBody" error={error} onRetry={refetch} />
      )}

      {/* Zero incoming requests is a normal state, not a failure (root
          CLAUDE.md) — same framing as a request with zero matched Excel
          lines. */}
      {isTrulyEmpty && (
        <EmptyState
          titleId="catalogManagerRequests.emptyTitle"
          bodyId="catalogManagerRequests.emptyBody"
        />
      )}

      {!isLoading && !isError && allRequests.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr className={styles.headerRow}>
              <th>{t('catalogManagerRequests.col.ref')}</th>
              <th>{t('catalogManagerRequests.col.neededBy')}</th>
              <th>{t('catalogManagerRequests.col.lines')}</th>
            </tr>
          </thead>
          <tbody>
            {allRequests.map((request) => (
              <tr key={request.id} className={styles.row}>
                <td className={`${styles.refCell} tabularNums`}>
                  <Link
                    to={`/incoming-requests/${request.id}`}
                    className={styles.refLink}
                    aria-label={t('catalogManagerRequests.openDetail')}
                  >
                    {request.ref_no}
                  </Link>
                </td>
                <td className="tabularNums">
                  {request.needed_by ? (
                    formatDate(request.needed_by, lang)
                  ) : (
                    <span className={styles.muted}>{t('catalogManagerRequests.neededByNone')}</span>
                  )}
                </td>
                <td className="tabularNums">{request.line_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
